import { useReducer, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchQuizQuestions,
  fetchQuizCategories,
  type QuizQuestion,
  type QuizMeta,
  type AnswerType,
} from '../../lib/quizData';
import { trackEvent, WEB_QUIZ_COMPLETE, WEB_QUIZ_START } from '../../lib/analytics';
import { useSEO } from '../../hooks/useSEO';
import { Chip } from '../../components/shared/Chip';
import { ScriptText } from '../../components/shared/ScriptText';
import { Button } from '../../components/shared/Button';
import { QuizProgress } from '../../components/quiz/QuizProgress';
import { QuizPlayerBadge } from '../../components/quiz/QuizPlayerBadge';
import { AnswerButton } from '../../components/quiz/AnswerButton';
import { MatchedItem } from '../../components/quiz/MatchedItem';
import { DownloadCTABlock } from '../../components/quiz/DownloadCTABlock';
import { QuizCategoryCard } from '../../components/quiz/QuizCategoryCard';
import quizBg from '../../assets/images/quiz-bg.png';
import styles from './QuizPlay.module.css';

type Step = 'loading' | 'intro' | 'player1' | 'handoff' | 'player2' | 'matching' | 'results';

interface State {
  step: Step;
  qIdx: number;
  herName: string;
  himName: string;
  p1Answers: Record<string, AnswerType>;
  p2Answers: Record<string, AnswerType>;
}

type Action =
  | { type: 'LOADED' }
  | { type: 'SET_HER_NAME'; name: string }
  | { type: 'SET_HIM_NAME'; name: string }
  | { type: 'START' }
  | { type: 'ANSWER_P1'; id: string; answer: AnswerType }
  | { type: 'HANDOFF' }
  | { type: 'ANSWER_P2'; id: string; answer: AnswerType }
  | { type: 'SHOW_MATCHING' }
  | { type: 'SHOW_RESULTS' }
  | { type: 'RESTART' };

function makeReducer(totalQ: number) {
  return function reducer(state: State, action: Action): State {
    switch (action.type) {
      case 'LOADED':
        return { ...state, step: 'intro' };
      case 'SET_HER_NAME':
        return { ...state, herName: action.name };
      case 'SET_HIM_NAME':
        return { ...state, himName: action.name };
      case 'START':
        return { ...state, step: 'player1', qIdx: 0 };
      case 'ANSWER_P1': {
        const updated = { ...state.p1Answers, [action.id]: action.answer };
        const next = state.qIdx + 1;
        if (next >= totalQ) {
          return { ...state, p1Answers: updated, step: 'handoff', qIdx: 0 };
        }
        return { ...state, p1Answers: updated, qIdx: next };
      }
      case 'HANDOFF':
        return { ...state, step: 'player2', qIdx: 0 };
      case 'ANSWER_P2': {
        const updated = { ...state.p2Answers, [action.id]: action.answer };
        const next = state.qIdx + 1;
        if (next >= totalQ) {
          return { ...state, p2Answers: updated, step: 'matching', qIdx: 0 };
        }
        return { ...state, p2Answers: updated, qIdx: next };
      }
      case 'SHOW_MATCHING':
        return { ...state, step: 'matching' };
      case 'SHOW_RESULTS':
        return { ...state, step: 'results' };
      case 'RESTART':
        return { step: 'intro', qIdx: 0, herName: state.herName, himName: state.himName, p1Answers: {}, p2Answers: {} };
      default:
        return state;
    }
  };
}

export function QuizPlay() {
  const { quizId } = useParams<{ quizId: string }>();
  const navigate = useNavigate();

  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [meta, setMeta] = useState<QuizMeta | null>(null);
  const [lockedCategories, setLockedCategories] = useState<QuizMeta[]>([]);

  const [state, dispatch] = useReducer(makeReducer(questions.length), {
    step: 'loading',
    qIdx: 0,
    herName: '',
    himName: '',
    p1Answers: {},
    p2Answers: {},
  });

  useSEO({
    title: meta ? `${meta.name} — Couple Quiz` : 'Couple Quiz',
    description: meta
      ? `Play the ${meta.name} together. ${meta.questionCount} intimate questions. Both Answer Quiz secretly, reveal mutual fantasies.`
      : 'Take the couples quiz together.',
  });

  useEffect(() => {
    if (!quizId) return;
    Promise.all([fetchQuizQuestions(quizId), fetchQuizCategories()]).then(
      ([qs, cats]) => {
        const catMeta = cats.find((c) => c.id === quizId);
        if (catMeta?.isLocked && !catMeta.isShareToUnlock) {
          navigate(`/quiz/${quizId}/locked`, { replace: true });
          return;
        }
        setMeta(catMeta ?? null);
        setQuestions(qs);
        setLockedCategories(cats.filter((c) => c.isLocked));
        dispatch({ type: 'LOADED' });
      },
    );
  }, [quizId, navigate]);

  useEffect(() => {
    if (state.step === 'matching') {
      const t = setTimeout(() => dispatch({ type: 'SHOW_RESULTS' }), 2600);
      return () => clearTimeout(t);
    }
  }, [state.step]);

  useEffect(() => {
    if (state.step !== 'results' || !quizId) return;
    const matchCount = questions.filter((q) => {
      const a1 = state.p1Answers[q.questionId];
      const a2 = state.p2Answers[q.questionId];
      return a1 && a2 && a1 !== 'No' && a2 !== 'No';
    }).length;
    void trackEvent(WEB_QUIZ_COMPLETE, { quiz_id: quizId, match_count: matchCount });
  }, [state.step, quizId, questions, state.p1Answers, state.p2Answers]);

  function handleStart() {
    if (quizId) {
      void trackEvent(WEB_QUIZ_START, { quiz_id: quizId });
    }
    dispatch({ type: 'START' });
  }

  const herName = state.herName || 'Her';
  const himName = state.himName || 'Him';

  const matches = questions.filter((q) => {
    const a1 = state.p1Answers[q.questionId];
    const a2 = state.p2Answers[q.questionId];
    return a1 && a2 && a1 !== 'No' && a2 !== 'No';
  });
  const p1Yes = Object.values(state.p1Answers).filter((v) => v === 'Yes').length;
  const p2Yes = Object.values(state.p2Answers).filter((v) => v === 'Yes').length;
  const compat = questions.length > 0
    ? Math.round((matches.length / questions.length) * 100)
    : 0;

  function handleP1Answer(answer: AnswerType) {
    const q = questions[state.qIdx];
    setTimeout(() => dispatch({ type: 'ANSWER_P1', id: q.questionId, answer }), 220);
  }

  function handleP2Answer(answer: AnswerType) {
    const q = questions[state.qIdx];
    setTimeout(() => dispatch({ type: 'ANSWER_P2', id: q.questionId, answer }), 220);
  }

  function subNames(text: string): string {
    return text
      .replace(/\$female/g, herName)
      .replace(/\$male/g, himName)
      .replace(/\$p1/g, herName)
      .replace(/\$p2/g, himName);
  }

  if (state.step === 'loading') {
    return (
      <div className={styles.stage} style={{ backgroundImage: `url(${quizBg})` }}>
        <div className={styles.card}>
          <p className={styles.body}>Loading quiz...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.stage} style={{ backgroundImage: `url(${quizBg})` }}>
      {/* ─── Intro ─── */}
      {state.step === 'intro' && (
        <div className={styles.card}>
          <Chip>{meta?.name ?? 'Couple Quiz'}</Chip>
          <h1 className={`display ${styles.title}`}>
            Discover each other's<br />
            <ScriptText rotate={-2}>fantasies.</ScriptText>
          </h1>
          {meta && (
            <p className={styles.body}>{meta.description}</p>
          )}
          <div className={styles.introStats}>
            <div className={styles.introStat}>
              <div className={styles.introStatValue}>{questions.length}</div>
              <div className={styles.introStatLabel}>questions</div>
            </div>
            <div className={styles.introStat}>
              <div className={styles.introStatValue}>~{Math.ceil(questions.length * 0.4)}</div>
              <div className={styles.introStatLabel}>minutes</div>
            </div>
            <div className={styles.introStat}>
              <div className={styles.introStatValue}>100%</div>
              <div className={styles.introStatLabel}>secret</div>
            </div>
          </div>
          <div className={styles.nameInputs}>
            <input
              className={styles.nameInput}
              placeholder="Her name (optional)"
              value={state.herName}
              onChange={(e) => dispatch({ type: 'SET_HER_NAME', name: e.target.value })}
            />
            <input
              className={styles.nameInput}
              placeholder="His name (optional)"
              value={state.himName}
              onChange={(e) => dispatch({ type: 'SET_HIM_NAME', name: e.target.value })}
            />
          </div>
          <Button variant="primary" onClick={handleStart}>
            ♥ {herName} goes first
          </Button>
          <p className={styles.disclaimer}>
            100% secret — what you say no to stays hidden forever
          </p>
        </div>
      )}

      {/* ─── Player 1 Questions ─── */}
      {state.step === 'player1' && questions[state.qIdx] && (
        <div className={styles.card}>
          <QuizProgress current={state.qIdx} total={questions.length} />
          <QuizPlayerBadge name={herName} color="var(--accent)" />
          <p className={styles.qCounter}>
            Question {state.qIdx + 1} of {questions.length}
          </p>
          <span className={styles.qTag}>{questions[state.qIdx].tag}</span>
          <h2 className={`display ${styles.question}`}>
            {subNames(questions[state.qIdx].questionPlayerTwo)}
          </h2>
          <div className={styles.answerRow}>
            <AnswerButton answer="No" onClick={() => handleP1Answer('No')} />
            <AnswerButton answer="Maybe" onClick={() => handleP1Answer('Maybe')} />
            <AnswerButton answer="Yes" onClick={() => handleP1Answer('Yes')} />
          </div>
          <p className={styles.privacy}>Secret until matched. Nobody sees a no.</p>
        </div>
      )}

      {/* ─── Handoff ─── */}
      {state.step === 'handoff' && (
        <div className={styles.card}>
          <div className={styles.handoffHeart}>♥</div>
          <Chip>{herName}'s answers are locked</Chip>
          <h2 className={`display ${styles.handoffTitle}`}>
            Hand the phone<br />to <ScriptText>{himName}.</ScriptText>
          </h2>
          <p className={styles.body}>
            {herName}'s answers are locked and hidden. Hand the phone to {himName} — same questions, fresh start.
          </p>
          <Button variant="primary" onClick={() => dispatch({ type: 'HANDOFF' })}>
            {himName}'s turn →
          </Button>
          <p className={styles.privacy}>{herName}'s answers are hidden until results.</p>
        </div>
      )}

      {/* ─── Player 2 Questions ─── */}
      {state.step === 'player2' && questions[state.qIdx] && (
        <div className={styles.card}>
          <QuizProgress current={state.qIdx} total={questions.length} />
          <QuizPlayerBadge name={himName} color="var(--him-purple)" />
          <p className={styles.qCounter}>
            Question {state.qIdx + 1} of {questions.length}
          </p>
          <span className={styles.qTag}>{questions[state.qIdx].tag}</span>
          <h2 className={`display ${styles.question}`}>
            {subNames(questions[state.qIdx].questionPlayerOne)}
          </h2>
          <div className={styles.answerRow}>
            <AnswerButton answer="No" onClick={() => handleP2Answer('No')} />
            <AnswerButton answer="Maybe" onClick={() => handleP2Answer('Maybe')} />
            <AnswerButton answer="Yes" onClick={() => handleP2Answer('Yes')} />
          </div>
          <p className={styles.privacy}>Secret until matched. Nobody sees a no.</p>
        </div>
      )}

      {/* ─── Matching Animation ─── */}
      {state.step === 'matching' && (
        <div className={styles.card}>
          <div className={styles.spinner}>♥</div>
          <Chip>Finding what you both want...</Chip>
          <p className={styles.body}>Matching your secret answers...</p>
        </div>
      )}

      {/* ─── Results ─── */}
      {state.step === 'results' && (
        <div className={styles.resultsCard}>
          {/* Results hero */}
          <div className={styles.resultsHero}>
            <h2 className={`display ${styles.resultsTitle}`}>
              {matches.length} shared{' '}
              <ScriptText rotate={-2}>
                {matches.length === 1 ? 'fantasy' : 'fantasies'}
              </ScriptText>
            </h2>
            <p className={styles.resultsCompat}>{compat}% match</p>
            <div className={styles.resultsMeta}>
              <span>{herName} · {p1Yes} yes</span>
              <span>|</span>
              <span>{himName} · {p2Yes} yes</span>
            </div>
          </div>

          {/* Matched bucket list */}
          {matches.length > 0 ? (
            <div className={styles.matchList}>
              <h3 className={styles.matchListTitle}>Your Sex Bucket List</h3>
              {matches.map((q, i) => (
                <MatchedItem
                  key={q.questionId}
                  index={i}
                  text={subNames(q.bucketList)}
                  tag={q.tag}
                  p1Answer={state.p1Answers[q.questionId]}
                  p2Answer={state.p2Answers[q.questionId]}
                />
              ))}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <p>No matches this round — try a spicier quiz? 😏</p>
              <Button variant="ghost" href="/quiz">Browse quizzes</Button>
            </div>
          )}

          {/* Download CTA */}
          <DownloadCTABlock />

          {/* Secondary actions */}
          <div className={styles.resultsCtas}>
            <Button variant="ghost" onClick={() => dispatch({ type: 'RESTART' })}>
              Try this one again
            </Button>
            <Button variant="ghost" href="/quiz">
              Play another quiz
            </Button>
          </div>

          {/* Locked quiz teasers */}
          {lockedCategories.length > 0 && (
            <div className={styles.teaserSection}>
              <h3 className={`display ${styles.teaserTitle}`}>
                Want more? <ScriptText>Unlock all.</ScriptText>
              </h3>
              <div className={styles.teaserGrid}>
                {lockedCategories.slice(0, 3).map((cat) => (
                  <QuizCategoryCard key={cat.id} quiz={cat} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
